import { useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, Bell, Camera, CircleUserRound, Cog, CreditCard, Download, FileText, HelpCircle, Lock, Save, ShieldCheck, Sparkles, Trash2 } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import { api, API_URL } from "../lib/api";
import { invalidateEntitlements } from "../lib/entitlements";

const LANGUAGES = [["es-MX", "Español (México)"], ["es-ES", "Español (España)"], ["en-US", "English (US)"]];
const TIMEZONES = ["America/Mexico_City", "America/Cancun", "America/Tijuana", "America/Monterrey", "America/Los_Angeles", "America/New_York", "UTC"];
const COUNTRIES = ["México", "Estados Unidos", "España", "Colombia", "Argentina", "Chile", "Perú"];
const CURRENCIES = ["MXN", "USD", "EUR", "CAD", "GBP"];
const THEMES = [["light", "Claro"], ["dark", "Oscuro"], ["auto", "Automático"]];
const DASHBOARDS = [["finanzas", "Finanzas"], ["ingresos", "Ingresos"], ["gastos", "Egresos"], ["facturas", "Facturas"]];
const REGIMES = ["RESICO", "Persona Física con Actividad Empresarial", "Régimen Simplificado", "Persona Moral", "Sueldos y Salarios", "Sin régimen"];

const TABS = [
  { id: "perfil", label: "Mi Perfil", icon: CircleUserRound },
  { id: "ajustes", label: "Ajustes", icon: Cog },
  { id: "notificaciones", label: "Notificaciones", icon: Bell },
  { id: "suscripcion", label: "Suscripción", icon: CreditCard },
  { id: "ayuda", label: "Ayuda", icon: HelpCircle },
];

const formatBytes = (mb) => (mb < 1 ? `${Math.max(0, mb * 1024).toFixed(0)} KB` : `${mb.toFixed(1)} MB`);

export default function MyProfilePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const searchTab = new URLSearchParams(location.search).get("tab") || "perfil";
  const [tab, setTab] = useState(searchTab);
  const [account, setAccount] = useState(null);
  const [form, setForm] = useState({});
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState("");
  const [passwordForm, setPasswordForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordNotice, setPasswordNotice] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const avatarInput = useRef(null);

  const load = () => api.get("/account/me", { withCredentials: true }).then((r) => {
    setAccount(r.data);
    setForm(r.data);
  }).catch(() => setError("No pudimos cargar tu cuenta."));

  useEffect(() => { load(); }, []);

  useEffect(() => {
    setTab(searchTab);
  }, [searchTab]);

  useEffect(() => {
    if (!account) return;
    if (account.avatar_uploaded) {
      const token = window.localStorage.getItem("session_token");
      fetch(`${API_URL}/account/avatar`, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} })
        .then((r) => (r.ok ? r.blob() : null))
        .then((blob) => blob && setAvatarUrl(URL.createObjectURL(blob)))
        .catch(() => setAvatarUrl(""));
    } else if (account.picture) {
      setAvatarUrl(account.picture);
    }
  }, [account?.avatar_uploaded, account?.picture]);

  const changeTab = (id) => {
    setTab(id);
    navigate(`/mi-perfil?tab=${id}`, { replace: true });
  };

  const setValue = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const save = async (event) => {
    event?.preventDefault();
    setSaving(true);
    setError("");
    try {
      const response = await api.put("/account/me", form, { withCredentials: true });
      setAccount(response.data);
      setForm(response.data);
      setNotice("Guardado. Tus preferencias se aplicarán en toda la app.");
      setTimeout(() => setNotice(""), 3500);
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos guardar los cambios.");
    } finally {
      setSaving(false);
    }
  };

  const uploadAvatar = async (file) => {
    if (!file) return;
    const body = new FormData();
    body.append("file", file);
    try {
      await api.post("/account/avatar", body, { withCredentials: true });
      setNotice("Foto actualizada.");
      load();
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos subir la foto.");
    }
  };

  const savePassword = async (event) => {
    event.preventDefault();
    setPasswordError("");
    setPasswordNotice("");
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setPasswordError("La confirmación no coincide con la nueva contraseña.");
      return;
    }
    setPasswordSaving(true);
    try {
      const body = { new_password: passwordForm.new_password };
      if (account?.password_enabled) body.current_password = passwordForm.current_password;
      const response = await api.post("/account/password", body, { withCredentials: true });
      setPasswordNotice(response.data?.message || "Contraseña actualizada.");
      setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
      load();
      setTimeout(() => setPasswordNotice(""), 4000);
    } catch (issue) {
      setPasswordError(issue.response?.data?.detail || "No pudimos actualizar la contraseña.");
    } finally {
      setPasswordSaving(false);
    }
  };

  const removePassword = async () => {
    const current_password = window.prompt("Escribe tu contraseña actual para desactivar el login local:");
    if (!current_password) return;
    setPasswordError("");
    setPasswordNotice("");
    try {
      await api.delete("/account/password", { data: { current_password }, withCredentials: true });
      setPasswordNotice("Contraseña desactivada. Sigues usando Google para entrar.");
      load();
      setTimeout(() => setPasswordNotice(""), 4000);
    } catch (issue) {
      setPasswordError(issue.response?.data?.detail || "No pudimos desactivar la contraseña.");
    }
  };

  const initials = useMemo(() => (account?.name || account?.email || "?").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase(), [account]);

  const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" }) : "—");

  return (
    <AppShell>
      <div className="page">
        <section className="profile-hero">
          <div>
            <small className="eyebrow">MI CUENTA</small>
            <h1>Tu perfil, <em>a tu manera.</em></h1>
            <p>Configura una vez y Mi Contador usa estos datos en todo el sistema.</p>
          </div>
        </section>

        <nav className="business-tabs" data-testid="my-profile-tabs">
          {TABS.map((t) => (
            <button key={t.id} type="button" className={tab === t.id ? "active" : ""} onClick={() => changeTab(t.id)} data-testid={`my-profile-tab-${t.id}`}>
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </nav>

        {notice && <p className="profile-notice" data-testid="my-profile-notice">{notice}</p>}
        {error && <p className="profile-error" data-testid="my-profile-error">{error}</p>}

        {tab === "perfil" && (
          <form onSubmit={save}>
            <section className="profile-section" data-testid="my-profile-identity">
              <h2>Identidad</h2>
              <p>Cambia tu foto y datos personales.</p>
              <div className="avatar-uploader">
                <span className="profile-menu-avatar">
                  {avatarUrl ? <img src={avatarUrl} alt="" /> : <b>{initials}</b>}
                </span>
                <div>
                  <button type="button" onClick={() => avatarInput.current?.click()} data-testid="my-profile-avatar-button">
                    <Camera size={15} style={{ marginRight: 6 }} /> Cambiar foto
                  </button>
                  <input ref={avatarInput} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: "none" }} onChange={(e) => uploadAvatar(e.target.files?.[0])} data-testid="my-profile-avatar-input" />
                  <p style={{ margin: "8px 0 0", fontSize: 12, color: "#6b7280" }}>JPG, PNG o WEBP · Hasta 3 MB.</p>
                </div>
              </div>
              <div className="profile-grid">
                <label>Nombre completo<input value={form.name || ""} onChange={(e) => setValue("name", e.target.value)} data-testid="my-profile-name" /></label>
                <label>Correo electrónico<input value={form.email || ""} disabled data-testid="my-profile-email" /></label>
                <label>Teléfono<input value={form.phone || ""} onChange={(e) => setValue("phone", e.target.value)} data-testid="my-profile-phone" /></label>
                <label>País<select value={form.country || "México"} onChange={(e) => setValue("country", e.target.value)} data-testid="my-profile-country">{COUNTRIES.map((c) => <option key={c}>{c}</option>)}</select></label>
                <label>Idioma preferido<select value={form.language || "es-MX"} onChange={(e) => setValue("language", e.target.value)} data-testid="my-profile-language">{LANGUAGES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
                <label>Zona horaria<select value={form.timezone || "America/Mexico_City"} onChange={(e) => setValue("timezone", e.target.value)} data-testid="my-profile-timezone">{TIMEZONES.map((t) => <option key={t}>{t}</option>)}</select></label>
              </div>
            </section>

            <section className="profile-section" data-testid="my-profile-account-info">
              <h2>Información de la cuenta</h2>
              <p>Datos que Mi Contador registra por ti.</p>
              <div className="profile-info-cards">
                <article><small>Miembro desde</small><strong>{formatDate(account?.created_at)}</strong></article>
                <article><small>Último acceso</small><strong>{formatDate(account?.last_login)}</strong></article>
                <article><small>Suscripción</small><strong>{account?.subscription?.plan?.toUpperCase() || "FREE"}</strong></article>
                <article><small>Almacenamiento</small><strong>{formatBytes(account?.storage_used_mb || 0)}</strong></article>
                <article><small>Negocios conectados</small><strong>{account?.businesses_count || 0}</strong></article>
                <article><small>Google</small><strong>{account?.connected_google ? "Conectado" : "—"}</strong></article>
              </div>
            </section>

            <button className="profile-save" type="submit" disabled={saving} data-testid="my-profile-save">
              <Save size={16} /> {saving ? "Guardando..." : "Guardar cambios"}
            </button>
          </form>
        )}

        {tab === "ajustes" && (
          <form onSubmit={save}>
            <section className="profile-section" data-testid="my-profile-preferences">
              <h2>Preferencias</h2>
              <p>Ajustes que Mi Contador usará en toda la app.</p>
              <div className="profile-grid">
                <label>Tema<select value={form.theme || "light"} onChange={(e) => setValue("theme", e.target.value)} data-testid="my-profile-theme">{THEMES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
                <label>Dashboard por defecto<select value={form.default_dashboard || "finanzas"} onChange={(e) => setValue("default_dashboard", e.target.value)} data-testid="my-profile-default-dashboard">{DASHBOARDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
                <label>Moneda por defecto<select value={form.default_currency || "MXN"} onChange={(e) => setValue("default_currency", e.target.value)} data-testid="my-profile-default-currency">{CURRENCIES.map((c) => <option key={c}>{c}</option>)}</select></label>
                <label>Régimen fiscal preferido<select value={form.default_tax_regime || ""} onChange={(e) => setValue("default_tax_regime", e.target.value)} data-testid="my-profile-default-regime"><option value="">Sin definir</option>{REGIMES.map((r) => <option key={r}>{r}</option>)}</select></label>
                <label>IVA por defecto (%)<input type="number" min="0" max="30" step="0.5" value={form.default_iva_rate ?? 16} onChange={(e) => setValue("default_iva_rate", Number(e.target.value))} data-testid="my-profile-default-iva" /></label>
              </div>
            </section>

            <button className="profile-save" type="submit" disabled={saving} data-testid="my-profile-save-settings"><Save size={16} /> Guardar ajustes</button>
          </form>
        )}

        {tab === "ajustes" && (
          <section className="profile-section" data-testid="my-profile-security">
            <h2>Seguridad y acceso</h2>
            <p>{account?.password_enabled
              ? "Tu cuenta tiene contraseña local activa. Puedes cambiarla en cualquier momento."
              : "Tu cuenta usa Google Sign-In. Puedes agregar una contraseña local como respaldo (opcional)."}</p>
            <div className="profile-grid" style={{ marginBottom: 14 }}>
              <label>Google conectado<input value={account?.email || ""} disabled data-testid="my-profile-google" /></label>
              <label>Estado contraseña local<input value={account?.password_enabled ? "Activada" : "No configurada"} disabled data-testid="my-profile-password-status" /></label>
            </div>
            {passwordNotice && <p className="profile-notice" data-testid="my-profile-password-notice">{passwordNotice}</p>}
            {passwordError && <p className="profile-error" data-testid="my-profile-password-error">{passwordError}</p>}
            <form onSubmit={savePassword} data-testid="my-profile-password-form">
              <div className="profile-grid">
                {account?.password_enabled && (
                  <label className="full">Contraseña actual
                    <input type="password" autoComplete="current-password" value={passwordForm.current_password} onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })} required data-testid="my-profile-password-current" />
                  </label>
                )}
                <label>Nueva contraseña
                  <input type="password" autoComplete="new-password" value={passwordForm.new_password} onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })} required minLength={8} data-testid="my-profile-password-new" />
                </label>
                <label>Confirmar nueva contraseña
                  <input type="password" autoComplete="new-password" value={passwordForm.confirm_password} onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })} required minLength={8} data-testid="my-profile-password-confirm" />
                </label>
              </div>
              <p style={{ display: "flex", alignItems: "center", gap: 8, color: "#6b7280", margin: "12px 0 0", fontSize: 13 }}>
                <Lock size={14} /> Mínimo 8 caracteres con al menos una letra y un número. Se almacena con hash bcrypt.
              </p>
              <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
                <button className="profile-save" type="submit" disabled={passwordSaving} data-testid="my-profile-password-save">
                  <Save size={16} /> {passwordSaving ? "Guardando..." : account?.password_enabled ? "Cambiar contraseña" : "Activar contraseña"}
                </button>
                {account?.password_enabled && (
                  <button type="button" className="cash-btn ghost" onClick={removePassword} data-testid="my-profile-password-remove">
                    Desactivar contraseña
                  </button>
                )}
              </div>
            </form>
          </section>
        )}

        {tab === "ajustes" && (
          <LegalAndDangerSection />
        )}

        {tab === "notificaciones" && (
          <EmailPreferencesSection />
        )}

        {tab === "suscripcion" && (
          <SubscriptionSection />
        )}

        {tab === "ayuda" && (
          <section className="profile-section" data-testid="my-profile-help">
            <h2>Centro de ayuda</h2>
            <p>Todo lo que necesitas saber sobre Mi Contador.</p>
            <ul style={{ padding: 0, listStyle: "none", display: "grid", gap: 12 }}>
              <li><b>El Conta</b> — Pregunta cualquier duda fiscal desde el botón flotante.</li>
              <li><b>Guía SAT</b> — Encuentra pasos detallados dentro del calendario, en cada fecha límite.</li>
              <li><b>Facturas</b> — Configura tu Perfil de Negocio y el sistema pre-llena tus facturas automáticamente.</li>
              <li><b>Exportar</b> — Desde Dashboard, Ingresos y Egresos puedes descargar CSV o PDF del mes.</li>
              <li><b>Soporte</b> — Escríbenos a hola@micontador.mx (equipo humano).</li>
            </ul>
          </section>
        )}
      </div>
    </AppShell>
  );
}

/** Legal downloads + Danger zone (delete account). Lives inside the "Ajustes" tab. */
function LegalAndDangerSection() {
  const navigate = useNavigate();
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  const deleteAccount = async () => {
    if (confirmText.trim() !== "ELIMINAR") {
      setError("Escribe ELIMINAR (mayúsculas) para confirmar.");
      return;
    }
    setDeleting(true);
    setError("");
    try {
      await api.delete("/account/me", { data: { confirm: "ELIMINAR" }, withCredentials: true });
      window.localStorage.removeItem("session_token");
      window.location.assign("/login");
    } catch (e) {
      setError(e.response?.data?.detail || "No pudimos eliminar la cuenta.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <section className="profile-section" data-testid="legal-downloads-section">
        <h2>Legal</h2>
        <p>Documentos oficiales de Mi Contador. Puedes descargar cada uno en PDF.</p>
        <div className="profile-info-cards">
          <article>
            <small>Aviso de Privacidad</small>
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Link className="cash-btn ghost" to="/legal/privacidad" data-testid="legal-view-privacy"><FileText size={14} /> Ver</Link>
              <Link className="cash-btn ghost" to="/legal/privacidad" data-testid="legal-download-privacy"><Download size={14} /> PDF</Link>
            </div>
          </article>
          <article>
            <small>Términos y Condiciones</small>
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Link className="cash-btn ghost" to="/legal/terminos" data-testid="legal-view-terms"><FileText size={14} /> Ver</Link>
              <Link className="cash-btn ghost" to="/legal/terminos" data-testid="legal-download-terms"><Download size={14} /> PDF</Link>
            </div>
          </article>
          <article>
            <small>Política de Cookies</small>
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Link className="cash-btn ghost" to="/legal/cookies" data-testid="legal-view-cookies"><FileText size={14} /> Ver</Link>
              <Link className="cash-btn ghost" to="/legal/cookies" data-testid="legal-download-cookies"><Download size={14} /> PDF</Link>
            </div>
          </article>
        </div>
        <p style={{ marginTop: 12, color: "#6b7280", fontSize: 13 }}>
          <ShieldCheck size={14} style={{ marginRight: 6, verticalAlign: -2 }} />
          Usa el botón "Descargar PDF" dentro de cada documento para guardarlo en tu dispositivo.
        </p>
      </section>

      <section className="profile-section danger-zone" data-testid="danger-zone-section" style={{ borderColor: "#fecaca" }}>
        <h2 style={{ color: "#b91c1c" }}><AlertTriangle size={18} style={{ marginRight: 6, verticalAlign: -3 }} />Eliminar cuenta</h2>
        <p style={{ color: "#7f1d1d" }}>
          Eliminar tu cuenta remueve de forma permanente tu perfil y toda la información asociada a Mi Contador, excepto cuando la ley requiera conservarla o para cumplir obligaciones legales.
        </p>
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 12, padding: 14 }}>
          <label style={{ display: "block", font: '700 12px "Nunito", sans-serif', color: "#7f1d1d", marginBottom: 6 }}>
            Escribe <b style={{ color: "#b91c1c" }}>ELIMINAR</b> para confirmar
          </label>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="ELIMINAR"
            data-testid="delete-account-confirm-input"
            style={{ width: "100%", padding: "10px 14px", border: "1px solid #fecaca", borderRadius: 10, font: '700 14px "Nunito", sans-serif' }}
          />
          {error && <p style={{ color: "#b91c1c", margin: "8px 0 0", fontSize: 13 }}>{error}</p>}
          <button
            type="button"
            onClick={deleteAccount}
            disabled={confirmText.trim() !== "ELIMINAR" || deleting}
            data-testid="delete-account-button"
            style={{
              marginTop: 12, display: "inline-flex", alignItems: "center", gap: 6,
              padding: "10px 18px", borderRadius: 10,
              background: confirmText.trim() === "ELIMINAR" ? "#ef4444" : "#fca5a5",
              color: "#fff", border: "none", cursor: confirmText.trim() === "ELIMINAR" ? "pointer" : "not-allowed",
              font: '800 13px "Nunito", sans-serif',
            }}
          >
            <Trash2 size={14} /> {deleting ? "Eliminando…" : "Eliminar cuenta permanentemente"}
          </button>
        </div>
      </section>
    </>
  );
}

/** Email preferences panel — connected to /api/emails/preferences with test-send button. */
function EmailPreferencesSection() {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/emails/preferences", { withCredentials: true })
      .then((r) => setPrefs(r.data))
      .catch(() => setPrefs({}));
  }, []);

  const toggle = async (key, value) => {
    setPrefs((cur) => ({ ...cur, [key]: value }));
    setSaving(true);
    setError("");
    try {
      const r = await api.put("/emails/preferences", { [key]: value }, { withCredentials: true });
      setPrefs(r.data.email_prefs || prefs);
      setNotice("Preferencias actualizadas.");
      setTimeout(() => setNotice(""), 2500);
    } catch (e) {
      setError(e.response?.data?.detail || "No pudimos guardar las preferencias.");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    setError("");
    try {
      await api.post("/emails/test", {}, { withCredentials: true });
      setNotice("Enviamos un correo de prueba a tu bandeja.");
      setTimeout(() => setNotice(""), 3500);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || "No pudimos enviar el correo.");
      setError(msg);
    } finally {
      setTesting(false);
    }
  };

  if (!prefs) return <div className="profile-section" data-testid="my-profile-notifications-loading">Cargando preferencias…</div>;

  const items = [
    { key: "tax_reminders", label: "Recordatorios fiscales", hint: "SAT · declaraciones · vencimientos del 24 de cada mes." },
    { key: "invoice_notifications", label: "Facturas", hint: "Cuando envías o recibes una factura." },
    { key: "monthly_summary", label: "Resumen mensual", hint: "Un correo con tus KPIs del mes." },
    { key: "ai_reports", label: "Reportes con IA", hint: "Insights personalizados y recomendaciones." },
    { key: "project_invitations", label: "Invitaciones a proyectos", hint: "Cuando alguien te invita a colaborar." },
    { key: "product_updates", label: "Novedades del producto", hint: "Tips y nuevas funciones (opcional)." },
  ];

  return (
    <section className="profile-section" data-testid="my-profile-notifications">
      <h2>Notificaciones por correo</h2>
      <p>Elige qué avisos quieres recibir. Los correos de <b>seguridad</b> y <b>verificación</b> siempre están activos.</p>
      {notice && <div style={{ background: "#ecfdf5", border: "1px solid #a7f3d0", color: "#065f46", padding: 10, borderRadius: 10, marginBottom: 10, font: '600 13px "Nunito", sans-serif' }} data-testid="email-prefs-notice">{notice}</div>}
      {error && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: 10, borderRadius: 10, marginBottom: 10, font: '600 13px "Nunito", sans-serif' }} data-testid="email-prefs-error">{error}</div>}
      <div className="profile-grid">
        {items.map(({ key, label, hint }) => (
          <label key={key} className="full" style={{ flexDirection: "row", display: "flex", justifyContent: "space-between", alignItems: "center", background: "#f9fafb", padding: "12px 16px", borderRadius: 12 }}>
            <span>
              <b style={{ display: "block", color: "#1f2937" }}>{label}</b>
              <small style={{ fontWeight: 500, color: "#6b7280" }}>{hint}</small>
            </span>
            <input
              type="checkbox"
              checked={prefs[key] !== false}
              onChange={(e) => toggle(key, e.target.checked)}
              disabled={saving}
              data-testid={`email-pref-${key.replaceAll("_", "-")}`}
            />
          </label>
        ))}
        <label className="full" style={{ flexDirection: "row", display: "flex", justifyContent: "space-between", alignItems: "center", background: "#eef2ff", padding: "12px 16px", borderRadius: 12, opacity: 0.9 }}>
          <span>
            <b style={{ display: "block", color: "#1f2937" }}>Correos de seguridad</b>
            <small style={{ fontWeight: 500, color: "#6b7280" }}>Nuevos inicios de sesión y actividad crítica. Siempre activos.</small>
          </span>
          <input type="checkbox" checked disabled data-testid="email-pref-security" />
        </label>
      </div>
      <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={sendTest}
          disabled={testing}
          data-testid="email-send-test-button"
          className="cash-btn ghost"
          style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          <Bell size={14} /> {testing ? "Enviando…" : "Enviarme un correo de prueba"}
        </button>
        <small style={{ color: "#6b7280", fontSize: 12 }}>Recibirás un correo de bienvenida para verificar que todo funciona.</small>
      </div>
      <SatAlertSettings />
    </section>
  );
}

/** Configurable SAT reminders — lead days + in-app bell toggle. */
function SatAlertSettings() {
  const LEAD_OPTIONS = [7, 5, 3, 1];
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/emails/sat-settings", { withCredentials: true })
      .then((r) => setSettings(r.data))
      .catch(() => setSettings({ sat_reminder_days: [5, 3, 1], in_app_alerts_enabled: true, email_reminders_enabled: true }));
  }, []);

  const save = async (patch) => {
    setSaving(true); setError("");
    try {
      const next = { ...settings, ...patch };
      setSettings(next);
      const r = await api.put("/emails/sat-settings", patch, { withCredentials: true });
      setSettings(r.data);
      setNotice("Recordatorios SAT actualizados.");
      setTimeout(() => setNotice(""), 2500);
    } catch (e) {
      setError(e.response?.data?.detail || "No pudimos guardar la configuración.");
    } finally {
      setSaving(false);
    }
  };

  const toggleDay = (day) => {
    if (!settings) return;
    const current = new Set(settings.sat_reminder_days || []);
    if (current.has(day)) current.delete(day); else current.add(day);
    const days = Array.from(current).sort((a, b) => b - a);
    save({ sat_reminder_days: days.length ? days : [5, 3, 1] });
  };

  if (!settings) return null;

  const selected = new Set(settings.sat_reminder_days || []);

  return (
    <div style={{ marginTop: 28 }} data-testid="sat-alert-settings">
      <h3 style={{ font: '800 16px "Nunito", sans-serif', color: "#0f172a", margin: "0 0 4px" }}>Alertas SAT dentro de la app</h3>
      <p style={{ color: "#6b7280", fontSize: 13, margin: "0 0 12px" }}>Elige cuántos días antes te avisamos con la campanita 🔔 y por correo.</p>
      {notice && <div style={{ background: "#ecfdf5", border: "1px solid #a7f3d0", color: "#065f46", padding: 10, borderRadius: 10, marginBottom: 10, font: '600 13px "Nunito", sans-serif' }} data-testid="sat-alerts-notice">{notice}</div>}
      {error && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: 10, borderRadius: 10, marginBottom: 10, font: '600 13px "Nunito", sans-serif' }} data-testid="sat-alerts-error">{typeof error === "string" ? error : JSON.stringify(error)}</div>}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
        {LEAD_OPTIONS.map((day) => {
          const on = selected.has(day);
          return (
            <button
              type="button"
              key={day}
              onClick={() => toggleDay(day)}
              disabled={saving}
              data-testid={`sat-lead-${day}`}
              style={{
                padding: "8px 14px", borderRadius: 999,
                border: on ? "2px solid #2563eb" : "2px solid #e5e7eb",
                background: on ? "#dbeafe" : "#fff",
                color: on ? "#1e3a8a" : "#374151",
                font: '700 13px "Nunito", sans-serif', cursor: "pointer",
              }}
            >
              {day === 1 ? "1 día antes" : `${day} días antes`}
            </button>
          );
        })}
      </div>
      <label className="full" style={{ flexDirection: "row", display: "flex", justifyContent: "space-between", alignItems: "center", background: "#f9fafb", padding: "12px 16px", borderRadius: 12 }}>
        <span>
          <b style={{ display: "block", color: "#1f2937" }}>Mostrar campanita en la app</b>
          <small style={{ fontWeight: 500, color: "#6b7280" }}>Verás las alertas SAT junto al avatar arriba.</small>
        </span>
        <input
          type="checkbox"
          checked={settings.in_app_alerts_enabled !== false}
          onChange={(e) => save({ in_app_alerts_enabled: e.target.checked })}
          disabled={saving}
          data-testid="sat-inapp-toggle"
        />
      </label>
    </div>
  );
}

/** Subscription tab: plan card + lifetime promo redemption. */
function SubscriptionSection() {
  const [status, setStatus] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = () => api.get("/subscription/status", { withCredentials: true })
    .then((r) => setStatus(r.data)).catch(() => setStatus(null));

  useEffect(() => { load(); }, []);

  const redeem = async () => {
    setBusy(true); setError(""); setNotice("");
    try {
      const r = await api.post("/subscription/redeem-promo",
        { code: code.trim().toUpperCase() }, { withCredentials: true });
      setStatus(r.data);
      setCode("");
      // Refresh the shared entitlements cache so nav bars, feature gates and
      // upgrade prompts across the app switch to unlocked immediately.
      invalidateEntitlements();
      setNotice(
        r.data.plan === "creador"
          ? `🎉 ¡Código ${r.data.promo_code} aplicado! Acceso completo a Mi Contador — de por vida, sin pagos.`
          : r.data.promo_discount === 100
            ? `🎉 ¡Código ${r.data.promo_code} aplicado! Acceso completo a Mi Contador — $0 MXN. Beneficio de por vida.`
            : `🎉 ¡Código ${r.data.promo_code} aplicado! ${r.data.promo_discount}% de descuento de por vida en todos los planes.`,
      );
    } catch (e) {
      setError(e.response?.data?.detail || "El código no es válido o ya expiró.");
    } finally {
      setBusy(false);
    }
  };

  const PLAN_PRICES = { independiente: 299, empresa: 699, pro: 1499, privado: 3500 };
  const displayPlan = status?.trial_active ? "trial" : (status?.plan || "free");
  const activePricePlan = status?.selected_plan || (status?.plan !== "free" && status?.plan !== "trial" ? status?.plan : "independiente");
  const basePrice = PLAN_PRICES[activePricePlan] || 0;
  const discount = status?.promo_discount || 0;
  const finalPrice = basePrice * (1 - discount / 100);

  return (
    <section className="profile-section" data-testid="my-profile-subscription">
      <h2>Suscripción y facturación</h2>
      <p>
        Tu plan actual: <b>{(status?.plan_name || displayPlan).toString()}</b>
        {status?.trial_active && <> · <b style={{ color: "#4169e1" }}>Prueba activa · quedan {status.days_left} día(s)</b></>}
      </p>

      {/* Active promo confirmation card */}
      {status?.promo_code && (
        <div
          data-testid="active-promo-card"
          style={{
            background: "linear-gradient(135deg, #ecfdf5, #eef2ff)",
            border: "1px solid #a7f3d0", borderRadius: 16, padding: "18px 22px",
            margin: "14px 0 20px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <span style={{ fontSize: 22 }}>🎉</span>
            <b style={{ font: '900 15px "Nunito", sans-serif', color: "#065f46" }}>
              Código <span style={{ color: "#0f172a" }}>{status.promo_code}</span> activo · de por vida
            </b>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 10 }}>
            <div style={{ background: "#fff", padding: 10, borderRadius: 10 }}>
              <small style={{ font: '700 10px "Nunito", sans-serif', color: "#6b7280", letterSpacing: "0.08em" }}>DESCUENTO</small>
              <strong style={{ display: "block", font: '900 22px "Nunito", sans-serif', color: "#10b981" }}>{status.promo_discount}%</strong>
            </div>
            <div style={{ background: "#fff", padding: 10, borderRadius: 10 }}>
              <small style={{ font: '700 10px "Nunito", sans-serif', color: "#6b7280", letterSpacing: "0.08em" }}>PRECIO FINAL / MES</small>
              <strong style={{ display: "block", font: '900 22px "Nunito", sans-serif', color: "#0f172a" }}>
                ${Math.round(finalPrice).toLocaleString("es-MX")} MXN
              </strong>
            </div>
          </div>
          <small style={{ display: "block", marginTop: 10, color: "#4b5563", font: '600 12px "Nunito", sans-serif' }}>
            {discount === 100
              ? "Acceso completo a todas las funciones sin costo · nunca expira · el descuento se conserva aunque cambies de plan."
              : `Pagas 50% del precio en cualquier plan · el descuento permanece activo mientras tu suscripción esté vigente y no se pierde al cambiar de plan.`}
          </small>
        </div>
      )}

      {/* Price breakdown when they have a selected plan */}
      {basePrice > 0 && !status?.trial_active && (
        <div style={{ background: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: 14, padding: 16, margin: "12px 0 20px" }} data-testid="price-breakdown">
          <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", font: '600 14px "Nunito", sans-serif', color: "#334155" }}>
            <span>Plan {activePricePlan}</span><span>${basePrice.toLocaleString("es-MX")} MXN/mes</span>
          </div>
          {discount > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", font: '600 14px "Nunito", sans-serif', color: "#10b981" }}>
              <span>Descuento {status.promo_code}</span><span>-{discount}%</span>
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0 4px", borderTop: "1px solid #e5e7eb", font: '900 15px "Nunito", sans-serif', color: "#0f172a" }}>
            <b>Total</b><b>${Math.round(finalPrice).toLocaleString("es-MX")} MXN/mes</b>
          </div>
          {status?.promo_lifetime && <small style={{ color: "#4169e1", font: '700 12px "Nunito", sans-serif' }}>De por vida ✨</small>}
        </div>
      )}

      {/* Promo redemption input */}
      {!status?.promo_code && (
        <div data-testid="promo-input-section" style={{ background: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: 14, padding: 18 }}>
          <h3 style={{ font: '900 15px "Nunito", sans-serif', color: "#0f172a", margin: "0 0 4px" }}>Código promocional</h3>
          <p style={{ font: '500 13px "Nunito", sans-serif', color: "#64748b", margin: "0 0 12px" }}>
            ¿Tienes un código? Escríbelo y aplica el beneficio a tu cuenta.
          </p>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              type="text"
              placeholder="Escribe tu código"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              disabled={busy}
              data-testid="subscription-promo-input"
              style={{ flex: 1, padding: "10px 14px", border: "1px solid #e5e7eb", borderRadius: 12, font: '700 14px "Nunito", sans-serif', textTransform: "uppercase" }}
            />
            <button
              type="button"
              onClick={redeem}
              disabled={!code.trim() || busy}
              data-testid="subscription-promo-apply"
              style={{ padding: "10px 22px", background: "#0f172a", color: "#fff", border: "none", borderRadius: 12, cursor: "pointer", font: '800 13px "Nunito", sans-serif' }}
            >
              {busy ? "Aplicando…" : "Aplicar"}
            </button>
          </div>
          {error && <p style={{ color: "#b91c1c", margin: "10px 0 0", font: '600 13px "Nunito", sans-serif' }} data-testid="subscription-promo-error">{error}</p>}
          {notice && <p style={{ color: "#065f46", margin: "10px 0 0", font: '600 13px "Nunito", sans-serif' }} data-testid="subscription-promo-notice">{notice}</p>}
        </div>
      )}

      {/* Manage subscription link */}
      <div style={{ marginTop: 20, display: "flex", gap: 10, flexWrap: "wrap" }}>
        <Link to="/pricing" className="cash-btn ghost" data-testid="subscription-view-plans">
          <Sparkles size={14} /> Ver planes y cambiar
        </Link>
      </div>
    </section>
  );
}
